import * as React from 'react';

import Button from '../../components/Button';

import { deriveChannelCounts, filterThreadsByChannel, type IChannelCount } from './threadChannelFilter';

export interface IThreadChannelFilterState<T> {
  filteredThreads: T[];
  channelCounts: IChannelCount[];
  activeChannel: string | null;
  setChannelFilter: (channel: string | null) => void;
}

/**
 * Owns the Threads-rail channel filter: `channelFilter` state, the reset-to-All
 * effect keyed on `userId`, and the derived filtered list + per-channel counts.
 *
 * Resetting on `userId` (rather than only in the picker handler) covers every
 * path that swaps the viewed user — the user picker AND the detail-owner effect
 * that follows a cross-user thread jump — so a stale channel from the prior user
 * can't leave the new user's rail filtered.
 *
 * `null` is the "All" sentinel. `activeChannel` auto-heals a stale filter: a
 * channel absent from the current user's loaded set (e.g. after a delete removed
 * its last thread) falls back to All rather than a blank rail.
 *
 * @param threads the currently loaded thread rows for the viewed user
 * @param userId the viewed user's id; a change resets the filter to All
 * @returns the filtered threads, per-channel counts, the active channel, and the setter
 */
export function useThreadChannelFilter<T extends { channel: string }>(
  threads: readonly T[],
  userId: string | null
): IThreadChannelFilterState<T> {
  const [channelFilter, setChannelFilter] = React.useState<string | null>(null);

  React.useEffect(() => {
    setChannelFilter(null);
  }, [userId]);

  const channelCounts = React.useMemo(() => deriveChannelCounts(threads), [threads]);
  const channelPresent = channelFilter !== null && channelCounts.some((c) => c.channel === channelFilter);

  // Clear a filter whose channel has left the loaded set (e.g. a delete removed
  // its last thread) so it can't silently re-apply if that channel reappears in
  // a later refetch. The `activeChannel` mask below keeps this render's rail
  // correct (shows All, not an empty list) until the state settles next tick.
  React.useEffect(() => {
    if (channelFilter !== null && !channelPresent) {
      setChannelFilter(null);
    }
  }, [channelFilter, channelPresent]);

  const activeChannel = channelPresent ? channelFilter : null;
  const filteredThreads = React.useMemo(() => filterThreadsByChannel(threads, activeChannel), [threads, activeChannel]);

  return { filteredThreads, channelCounts, activeChannel, setChannelFilter };
}

interface IThreadChannelFilterBarProps {
  counts: IChannelCount[];
  activeChannel: string | null;
  totalCount: number;
  truncated: boolean;
  onSelect: (channel: string | null) => void;
}

/**
 * Single-select channel filter for the Threads rail: an `All` chip plus one
 * chip per present channel, each with its count. Counts are suffixed `+` when
 * the loaded window is truncated (`truncated`), since they are then lower bounds.
 * Only the left rail is filtered — the detail pane renders off the selected
 * thread id, so a selected thread hidden by the filter still shows on the right.
 */
const ThreadChannelFilterBar = ({
  counts,
  activeChannel,
  totalCount,
  truncated,
  onSelect,
}: IThreadChannelFilterBarProps) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-fg-quaternary mr-0.5 text-[10px] uppercase tracking-wider">Channel</span>
    <Button color={activeChannel === null ? 'primary' : 'secondary'} size="xs" onClick={() => onSelect(null)}>
      {`All · ${totalCount}${truncated ? '+' : ''}`}
    </Button>
    {counts.map((c) => (
      <Button
        key={c.channel}
        color={activeChannel === c.channel ? 'primary' : 'secondary'}
        size="xs"
        onClick={() => onSelect(c.channel)}
      >
        {`${c.channel} · ${c.count}${truncated ? '+' : ''}`}
      </Button>
    ))}
  </div>
);

export default ThreadChannelFilterBar;
